import { useQuery } from '@tanstack/react-query';
import momentTZ from 'moment-timezone';
import { useEffect } from 'react';

import { useAppDispatch } from 'redux/hooks';
import { updatePreviewChartData } from 'redux/slice/previewDataControllerSlice';
import api, { oas3Api, ROUTES } from 'utils/api';

const { apiPath, getTypedApiData } = ROUTES.POST_LATEST_GRAPH_DATA;

interface SensorDataRequest {
  sensorClass: string;
  quantityName: string;
  nodeId: number;
}

type Dispatch = ReturnType<typeof useAppDispatch>;

const getWindDirection = (deg: number) => {
  if (deg >= 22.5 && deg < 67.5) return 'NE';
  if (deg >= 67.5 && deg < 112.5) return 'E';
  if (deg >= 112.5 && deg < 157.5) return 'SE';
  if (deg >= 157.5 && deg < 202.5) return 'S';
  if (deg >= 202.5 && deg < 247.5) return 'SW';
  if (deg >= 247.5 && deg < 292.5) return 'W';
  if (deg >= 292.5 && deg < 337.5) return 'NW';
  return 'N';
};

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const padDatePart = (value: number, length = 2) =>
  value.toString().padStart(length, '0');

const formatMillisecond = (date: Date) =>
  `.${padDatePart(date.getMilliseconds(), 3)}`;
const formatSecond = (date: Date) => `:${padDatePart(date.getSeconds())}`;
const formatMinute = (date: Date) =>
  `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
const formatDay = (date: Date) =>
  `${padDatePart(date.getDate())} ${MONTHS_SHORT[date.getMonth()]}`;
const formatHour = (date: Date) => `${formatMinute(date)}, ${formatDay(date)}`;
const formatWeek = formatDay;
const formatMonth = (date: Date) => MONTHS_LONG[date.getMonth()];
const formatYear = (date: Date) => date.getFullYear().toString();

const isAfterSecondStart = (date: Date) => date.getMilliseconds() > 0;
const isAfterMinuteStart = (date: Date) =>
  date.getSeconds() > 0 || isAfterSecondStart(date);
const isAfterHourStart = (date: Date) =>
  date.getMinutes() > 0 || isAfterMinuteStart(date);
const isAfterDayStart = (date: Date) =>
  date.getHours() > 0 || isAfterHourStart(date);
const isAfterWeekStart = (date: Date) =>
  date.getDay() > 0 || isAfterDayStart(date);
const isAfterMonthStart = (date: Date) =>
  date.getDate() > 1 || isAfterDayStart(date);
const isAfterYearStart = (date: Date) =>
  date.getMonth() > 0 || isAfterMonthStart(date);

function multiFormat(date: Date) {
  return (
    isAfterSecondStart(date)
      ? formatMillisecond
      : isAfterMinuteStart(date)
        ? formatSecond
        : isAfterHourStart(date)
          ? formatMinute
          : isAfterDayStart(date)
            ? formatHour
            : isAfterMonthStart(date)
              ? isAfterWeekStart(date)
                ? formatDay
                : formatWeek
              : isAfterYearStart(date)
                ? formatMonth
                : formatYear
  )(date);
}

const mapSuccessfulApiResponse = (
  response: ReturnType<typeof getTypedApiData>
) => {
  const ACCURACY = 2;
  return response.map(({ qcValue, timestamp }) => {
    const rawValue = Number(qcValue).toFixed(ACCURACY);
    const timeStamp = Number(timestamp);
    return {
      y: rawValue,
      x: momentTZ.utc(Number(timeStamp)).tz('Asia/Singapore')
    };
  });
};

const useLandingPageData = ({
  location,
  parameter,
  dispatch
}: {
  parameter: string;
  location: string;
  dispatch: Dispatch;
}) => {
  const { data, status } = useQuery({
    queryKey: ['home', 'graph', 'graph-data', location, parameter],
    queryFn: async () => {
      return await api({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}${apiPath}`,
        data: {
          location,
          quantity: parameter
        },
        dontShowGlobalLoader: true
      } as any).then((res) => res.data);
    },
    refetchInterval: 5 * 60 * 1000
  });

  useEffect(() => {
    if (status === 'success' && data) {
      dispatch(
        updatePreviewChartData({
          location,
          quantity: parameter,
          data: mapSuccessfulApiResponse(data)
        })
      );
    }
  }, [data, status]);
};

export { getWindDirection, multiFormat, useLandingPageData };
